import React, { useEffect, useState } from "react";
import { Printer, Star } from "lucide-react";
import {
  BarDistributionChart,
  ChartCard,
  DonutChart,
  StackedBarChart,
  StatTile,
  NEUTRAL,
  ORDINAL,
  percentOf,
  seriesColor,
} from "../component/charts";
import maintenanceReport, { TOTAL_APPLICANTS } from "../constant/maintenanceReportData";
import CustomTabs from "../component/tabs";

/**
 * Maintenance Manager — Applicant Pool Summary.
 *
 * Same report-paper shell as the candidate summary report, but the body is an
 * analytics view: headline figures, then one chart per dimension of the pool.
 * All chart marks are built from the reusable pieces in component/charts, which
 * read their colours from the --viz-* tokens in main.css.
 */

const TOTAL_QUIZ_TAKERS = maintenanceReport.quizScores.reduce(
  (acc, d) => acc + d.value,
  0
);

/* One tab per analytics block; the report shell (header, headline figures,
   snapshot and shortlisting lens) stays outside the tab strip. */
const REPORT_TABS = [
  { name: "Experience & Availability", isRequired: false, isVisiable: true },
  { name: "Education Profile", isRequired: false, isVisiable: true },
  { name: "Industry Background", isRequired: false, isVisiable: true },
  { name: "Assessment & Fitment", isRequired: false, isVisiable: true },
  { name: "Role-Specific Screening", isRequired: false, isVisiable: true },
  { name: "Key Distributions", isRequired: false, isVisiable: true },
];

export default function MaintenanceReport() {
  const {
    meta,
    headline,
    experience,
    buyOut,
    education,
    discipline,
    industry,
    quizScores,
    fitment,
    screening,
    narrative,
    keyDistributions,
    shortlistLens,
  } = maintenanceReport;

  const [activeTab, setActiveTab] = useState(REPORT_TABS[0].name);

  useEffect(() => {
    document.title = "Maintenance Manager - Applicant Pool Summary";
  }, []);

  const screeningSeries = [
    { key: "yes", label: "Yes", color: seriesColor(0) },
    { key: "no", label: "No", color: seriesColor(1) },
    { key: "na", label: "N/A", color: NEUTRAL() },
  ];

  /* Ordered bands: best fitment reads darkest on the single-hue ramp. */
  const fitmentColors = [...ORDINAL()].reverse();

  return (
    <div className="ds-wrapper pt-4 pb-5 px-3">
      <div className="report-paper">
        {/* HEADER */}
        <div className="row g-0 pb-3 border-bottom mb-4">
          <div className="col-8">
            <div className="report-eyebrow mb-1">{meta.eyebrow}</div>
            <h1 className="text-primary">{meta.title}</h1>
            <div className="report-subtitle mt-1">{meta.subtitle}</div>
            <div className="report-subtitle">{meta.source}</div>
          </div>
          {/* <div className="col-4 d-flex justify-content-end align-items-start">
            <button
              type="button"
              className="btn btn-primary shadow-sm"
              onClick={() => window.print()}
              data-html2canvas-ignore="true"
            >
              <Printer size={14} /> Print / Save PDF
            </button>
          </div> */}
        </div>

        {/* HEADLINE FIGURES */}
        <div className="stat-tile-row mb-4">
          {headline.map((stat) => (
            <StatTile key={stat.label} {...stat} />
          ))}
        </div>

        {/* SNAPSHOT */}
        <section className="mb-4">
          <h3>Snapshot</h3>
          <p className="text-base lh-sm mb-0">{narrative.snapshot}</p>
        </section>

        {/* CHART TABS */}
        <div className="mb-4">
          <CustomTabs
            tabs={REPORT_TABS}
            activeTab={activeTab}
            handleTabs={(e, tab) => {
              if (tab === activeTab) return;
              setActiveTab(tab);
            }}
          />
        </div>

        {/* EXPERIENCE & AVAILABILITY */}
        {activeTab === "Experience & Availability" && (
        <section className="mb-4">
          <p className="text-base lh-sm">{narrative.experience}</p>
          <div className="chart-grid">
            <ChartCard
              title="Years of Experience"
              subtitle="Applicants per experience band"
              tableRows={experience}
              total={TOTAL_APPLICANTS}
            >
              <BarDistributionChart
                data={experience}
                orientation="column"
                valueAxisLabel="Applicants"
                total={TOTAL_APPLICANTS}
              />
            </ChartCard>

            <ChartCard
              title="Notice Buy-out"
              subtitle="Availability of a notice-period buy-out"
              tableRows={buyOut}
              total={TOTAL_APPLICANTS}
            >
              <BarDistributionChart
                data={buyOut}
                orientation="column"
                valueAxisLabel="Applicants"
                total={TOTAL_APPLICANTS}
                colorFor={(_, index) => seriesColor(index)}
              />
            </ChartCard>
          </div>
        </section>
        )}

        {/* EDUCATION PROFILE */}
        {activeTab === "Education Profile" && (
        <section className="mb-4">
          <p className="text-base lh-sm">{narrative.education}</p>
          <div className="chart-grid">
            <ChartCard
              title="Education Level"
              subtitle="Highest qualification held"
              tableRows={education}
              total={TOTAL_APPLICANTS}
            >
              <DonutChart
                data={education}
                colors={[
                  seriesColor(0),
                  seriesColor(1),
                  seriesColor(2),
                  seriesColor(3),
                  NEUTRAL(),
                ]}
                centerValue={TOTAL_APPLICANTS}
                centerLabel="applicants"
                total={TOTAL_APPLICANTS}
              />
            </ChartCard>

            <ChartCard
              title="Engineering Discipline"
              subtitle="Primary discipline of study"
              tableRows={discipline}
              total={TOTAL_APPLICANTS}
            >
              <BarDistributionChart
                data={discipline}
                orientation="bar"
                height={260}
                categoryWidth={116}
                valueAxisLabel="Applicants"
                total={TOTAL_APPLICANTS}
                neutralKeys={["Others"]}
              />
            </ChartCard>
          </div>
        </section>
        )}

        {/* INDUSTRY BACKGROUND */}
        {activeTab === "Industry Background" && (
        <section className="mb-4">
          <p className="text-base lh-sm">{narrative.industry}</p>
          <ChartCard
            title="Current Industry"
            subtitle="Sector the applicant works in today"
            tableRows={industry}
            total={TOTAL_APPLICANTS}
          >
            <BarDistributionChart
              data={industry}
              orientation="bar"
              height={320}
              categoryWidth={150}
              valueAxisLabel="Applicants"
              total={TOTAL_APPLICANTS}
              neutralKeys={["Other industries", "N/A"]}
            />
          </ChartCard>
        </section>
        )}

        {/* ASSESSMENT & FITMENT */}
        {activeTab === "Assessment & Fitment" && (
        <section className="mb-4">
          <p className="text-base lh-sm">{narrative.assessment}</p>
          <div className="chart-grid">
            <ChartCard
              title="Technical Quiz Score"
              subtitle={`Score out of 10 · ${TOTAL_QUIZ_TAKERS} applicants sat the quiz`}
              tableRows={quizScores}
              tableHeaders={["Score", "Applicants", "Share"]}
              total={TOTAL_QUIZ_TAKERS}
            >
              <BarDistributionChart
                data={quizScores}
                orientation="column"
                valueAxisLabel="Applicants"
                total={TOTAL_QUIZ_TAKERS}
              />
            </ChartCard>

            <ChartCard
              title="Overall Fitment Score"
              subtitle="Fitment band across the full pool"
              tableRows={fitment}
              tableHeaders={["Band", "Applicants", "Share"]}
              total={TOTAL_APPLICANTS}
            >
              <DonutChart
                data={fitment}
                colors={fitmentColors}
                centerValue={42}
                centerLabel="top-fit"
                total={TOTAL_APPLICANTS}
              />
            </ChartCard>
          </div>
        </section>
        )}

        {/* ROLE-SPECIFIC SCREENING */}
        {activeTab === "Role-Specific Screening" && (
        <section className="mb-4">
          <p className="text-base lh-sm">{narrative.screening}</p>
          <ChartCard
            title="Screening Question Responses"
            subtitle="Four custom questions · all 174 applicants"
            tableRows={screening.map((q) => ({ name: q.name, value: q.yes }))}
            tableHeaders={["Question", 'Answered "Yes"', "Share"]}
            total={TOTAL_APPLICANTS}
          >
            <StackedBarChart data={screening} series={screeningSeries} />
          </ChartCard>
        </section>
        )}

        {/* KEY DISTRIBUTIONS */}
        {activeTab === "Key Distributions" && (
        <section className="mb-4">
          <div className="table-container shadow-sm">
            <table className="ant-table">
              <thead>
                <tr>
                  <th width="30%">Dimension</th>
                  <th width="40%">Leading segment</th>
                  <th width="15%" className="text-end">
                    Count
                  </th>
                  <th width="15%" className="text-end">
                    Share
                  </th>
                </tr>
              </thead>
              <tbody>
                {keyDistributions.map((row) => (
                  <tr key={row.dimension}>
                    <td className="attr-col">{row.dimension}</td>
                    <td>{row.segment}</td>
                    <td className="text-end fw-bold">{row.count}</td>
                    <td className="text-end text-muted fw-bold">
                      {percentOf(row.count, TOTAL_APPLICANTS)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        )}

        {/* SHORTLISTING LENS */}
        <section>
          <div className="hl-box shadow-sm">
            <div className="hl-header text-primary">
              Suggested Shortlisting Lens
            </div>
            <div className="hl-content">
              <div className="hl-item">
                <div>
                  To convert {TOTAL_APPLICANTS} applicants into a manageable
                  interview slate, prioritise candidates who meet all of the
                  following:
                </div>
              </div>
              {shortlistLens.map((item, i) => (
                <div className="hl-item" key={i}>
                  <Star
                    size={10}
                    className="hl-bullet"
                    fill="currentColor"
                    style={{
                      marginTop: "3px",
                      minWidth: "10px",
                      minHeight: "10px",
                      flexShrink: 0,
                    }}
                  />
                  <div>{item}</div>
                </div>
              ))}
              <div className="hl-item">
                <div className="text-muted fst-italic">
                  Applying these filters together typically narrows the pool to a
                  15–25 candidate shortlist suitable for first-round interviews.
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
